import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useProjectStore } from '../../contexts/projectStoreProvider';
import { useProjects } from '../../hooks/useProjects';
import { useSettings } from '@/contexts/SettingsContext';
import { formatDateByPreference } from '@/utils/dateFormat';
import { Schedule } from '../../types/project';
import { Activity, ScheduleActivity } from '../../types/index';
import { updateScheduleStatus, getScheduleActivities, getScheduleRelationships } from '../../api/schedule';
import { ActivityTable } from '@/components/ActivityTable'
import ResourceAnalytics from '@/components/resources/ResourceAnalytics';
import { InlineSpinner } from '@/components/ui/loading';

// Define tab type
type DetailTab = 'activities' | 'resources' | 'calendar' | 'wbs';

// Extend Schedule type for this component to allow any status and activities
interface ScheduleWithActivities extends Omit<Schedule, 'status'> {
  status?: string;
  activities?: any[];
}

const ScheduleDetail: React.FC = () => {
  const { projectId, scheduleId } = useParams<{ projectId: string; scheduleId: string }>();
  const { settings } = useSettings();
  const { projects, currentProject: storeCurrentProject, setCurrentProject } = useProjectStore();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<DetailTab>('activities');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [isLoadingRelationships, setIsLoadingRelationships] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activities, setActivities] = useState<ScheduleActivity[]>([]);
  const [relationships, setRelationships] = useState<any[]>([]);
  
  // Get current project from URL param or store
  const currentProject = projectId 
    ? projects.find(p => p.id === projectId) || storeCurrentProject
    : storeCurrentProject;
  
  // Debug logging for currentProject
  console.log('=== ScheduleDetail Debug ===');
  console.log('projectId from URL:', projectId);
  console.log('projects from store:', projects.length);
  console.log('storeCurrentProject:', storeCurrentProject);
  console.log('currentProject determined:', currentProject);
  console.log('currentProject.id:', currentProject?.id);
  
  const currentSchedule = currentProject?.schedules?.find(s => s.id === scheduleId) as ScheduleWithActivities;
  
  // Set current project in store if it's not already set
  useEffect(() => {
    if (currentProject && (!storeCurrentProject || storeCurrentProject.id !== currentProject.id)) {
      setCurrentProject(currentProject);
    }
  }, [currentProject, storeCurrentProject, setCurrentProject]);
  
  // Fetch activities when component loads or schedule changes
  useEffect(() => {
    if (scheduleId) {
      fetchActivities();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchActivities recreated each render; scheduleId is key
  }, [scheduleId]);
  
  useEffect(() => {
    if (!currentSchedule) {
      setError('Schedule not found');
    } else {
      setError(null);
    }
  }, [currentSchedule]);
  
  // Fetch relationships when component loads or schedule changes
  useEffect(() => {
    if (scheduleId) {
      fetchRelationships();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchRelationships recreated each render; scheduleId is key
  }, [scheduleId]);
  
  // The backend now handles all date and duration logic properly
  // No need for frontend fixes - just pass through the data
  const fixActivityData = (activitiesData: ScheduleActivity[]): ScheduleActivity[] => {
    return activitiesData;
  };

  // Fetch activities from backend
  const fetchActivities = async () => {
    if (!scheduleId) return;
    
    setIsLoadingActivities(true);
    setError(null);
    
    try {
      console.log('Fetching activities for schedule:', scheduleId);
      const activitiesData = await getScheduleActivities(scheduleId);
      console.log('Fetched activities:', activitiesData);
      
      // Fix the data for completed activities
      const fixedActivities = fixActivityData(activitiesData);
      console.log('Fixed activities:', fixedActivities);
      
      setActivities(fixedActivities);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      setError('Failed to load activities. Please try again.');
      // Set empty array to avoid undefined errors
      setActivities([]);
    } finally {
      setIsLoadingActivities(false);
    }
  };
  
  // Fetch relationships from backend
  const fetchRelationships = async () => {
    if (!scheduleId) return;
    
    setIsLoadingRelationships(true);
    setError(null);
    
    try {
      console.log('Fetching relationships for schedule:', scheduleId);
      const relationshipsData = await getScheduleRelationships(scheduleId);
      console.log('Fetched relationships:', relationshipsData);
      
      setRelationships(relationshipsData);
    } catch (error) {
      console.error('Failed to fetch relationships:', error);
      // Don't set error for relationships as it's not critical
      setRelationships([]);
    } finally {
      setIsLoadingRelationships(false);
    }
  };
  
  // If project or schedule not found, show error
  if (!currentProject || !currentSchedule) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          {!currentProject ? 'Project not found.' : 'Schedule not found.'}
        </div>
        <div className="mt-4">
          <Link to="/projects" className="text-blue-600 hover:text-blue-800">
            Return to Projects
          </Link>
        </div>
      </div>
    );
  }
  
  // Get schedule status color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'Current': return 'bg-green-100 text-green-800';
      case 'Baseline': return 'bg-blue-100 text-blue-800';
      case 'Historical': return 'bg-gray-100 text-gray-800';
      case 'What-If': return 'bg-purple-100 text-purple-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Handle setting schedule as current
  const handleSetAsCurrent = async () => {
    if (!currentSchedule || currentSchedule.status === 'Current') {
      return; // Already current
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Call the real API
      const updatedSchedule = await updateScheduleStatus(currentSchedule.id, 'current');
      
      // Update the project in the store
      if (currentProject && currentProject.schedules) {
        const updatedSchedules = currentProject.schedules.map(s => {
          if (s.id === currentSchedule.id) {
            return { ...s, status: 'Current' };
          } else if (s.status === 'Current') {
            return { ...s, status: 'Historical' };
          }
          return s;
        });
        
        setCurrentProject({
          ...currentProject,
          schedules: updatedSchedules
        });
      }
      
      console.log('Schedule set as current successfully');
    } catch (error) {
      console.error('Failed to set schedule as current:', error);
      setError('Failed to set schedule as current. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle setting schedule as baseline
  const handleSetAsBaseline = async () => {
    if (!currentSchedule || currentSchedule.status === 'Baseline') {
      return; // Already baseline
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Call the real API
      const updatedSchedule = await updateScheduleStatus(currentSchedule.id, 'baseline');
      
      // Update the project in the store
      if (currentProject && currentProject.schedules) {
        const updatedSchedules = currentProject.schedules.map(s => {
          if (s.id === currentSchedule.id) {
            return { ...s, status: 'Baseline' };
          } else if (s.status === 'Baseline') {
            return { ...s, status: 'Historical' };
          }
          return s;
        });
        
        setCurrentProject({
          ...currentProject,
          schedules: updatedSchedules
        });
      }
      
      console.log('Schedule set as baseline successfully');
    } catch (error) {
      console.error('Failed to set schedule as baseline:', error);
      setError('Failed to set schedule as baseline. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString?: string): string =>
    dateString ? formatDateByPreference(dateString, settings.dateFormat, { timeZone: settings.timezone }) : 'N/A';

  // Format duration for display
  const formatDuration = (duration?: number): string => {
    if (duration === undefined || duration === null) return 'N/A';
    return `${duration} days`;
  };

  // Format percentage for display
  const formatPercentage = (percent?: number): string => {
    if (percent === undefined || percent === null) return 'N/A';
    return `${percent}%`;
  };

  return (
    <div className="p-6">
      {/* Breadcrumb navigation */}
      <div className="mb-4 flex items-center text-sm text-gray-500">
        <Link to="/projects" className="hover:text-nb-accent-primary">Projects</Link>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <Link to={`/projects/${projectId}`} className="hover:text-primary">{currentProject.name}</Link>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <Link to={`/projects/${projectId}/schedules`} className="hover:text-primary">Schedules</Link>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="font-medium text-gray-900">{currentSchedule.name}</span>
      </div>
      
      {/* Schedule header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center">
              <h1 className="text-2xl font-semibold text-gray-800 mr-3">{currentSchedule.name}</h1>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(currentSchedule.status || '')}`}>
                {currentSchedule.status === 'ready' ? 'Ready' : 
                 currentSchedule.status === 'completed' ? 'Completed' : 
                 currentSchedule.status}
              </span>
            </div>
            <p className="text-gray-500 mt-1">{currentSchedule.description || 'No description'}</p>
          </div>
          <div className="flex space-x-2">
            <div className="flex space-x-2">
              {currentSchedule.status !== 'Current' && (
                <button 
                  onClick={handleSetAsCurrent}
                  disabled={isLoading}
                  className={`px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md transition-colors text-sm ${
                    isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                  }`}
                >
                  Set as Current
                </button>
              )}
              {currentSchedule.status !== 'Baseline' && (
                <button 
                  onClick={handleSetAsBaseline}
                  disabled={isLoading}
                  className={`px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md transition-colors text-sm ${
                    isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                  }`}
                >
                  Set as Baseline
                </button>
              )}
            </div>
            <button className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm">
              Download
            </button>
            <button className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors text-sm flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit Schedule
            </button>
          </div>
        </div>
      </div>
      
      {/* Schedule metadata */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Start Date</p>
          <p className="text-lg font-semibold mt-1">
            {currentSchedule.start_date ? formatDateByPreference(currentSchedule.start_date as string, settings.dateFormat, { timeZone: settings.timezone }) : 'N/A'}
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">End Date</p>
          <p className="text-lg font-semibold mt-1">
            {currentSchedule.end_date ? formatDateByPreference(currentSchedule.end_date as string, settings.dateFormat, { timeZone: settings.timezone }) : 'N/A'}
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Duration</p>
          <p className="text-lg font-semibold mt-1">
            {currentSchedule.duration ? `${currentSchedule.duration} days` : 'N/A'}
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Activities</p>
          <p className="text-lg font-semibold mt-1">
            {typeof currentSchedule.activity_count === 'number' ? currentSchedule.activity_count : 'N/A'}
          </p>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('activities')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'activities' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Activities ({activities.length})
          </button>
          <button
            onClick={() => setActiveTab('resources')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'resources' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Resources
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'calendar' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Calendar
          </button>
          <button
            onClick={() => setActiveTab('wbs')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'wbs' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            WBS
          </button>
        </nav>
      </div>
      
      {/* Tab content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {activeTab === 'activities' && (
          <div className="overflow-x-auto">
            {isLoadingActivities ? (
              <div className="flex justify-center items-center h-32 gap-2">
                <InlineSpinner size="md" />
                <span className="text-sm text-muted-foreground">Loading activities...</span>
              </div>
            ) : activities.length > 0 ? (
              <ActivityTable 
                activities={activities}
              />
            ) : (
              <div className="text-center py-12">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Activities Found</h3>
                <p className="text-gray-500 mb-4">
                  This schedule doesn't have any activities loaded yet.
                </p>
                <button
                  onClick={fetchActivities}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors"
                >
                  Refresh Activities
                </button>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'resources' && (
          <div className="p-6">
            {projectId ? (
              <ResourceAnalytics projectId={projectId} />
            ) : (
              <div className="text-center text-gray-500">
                Project ID not available for resource analytics.
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'calendar' && (
          <div className="p-6 text-center">
            <div className="bg-gray-100 p-12 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h2 className="text-xl font-medium text-gray-700 mb-2">Calendar View</h2>
              <p className="text-gray-500">
                Calendar view with activity assignments will be displayed here.
              </p>
            </div>
          </div>
        )}
        
        {activeTab === 'wbs' && (
          <div className="p-6 text-center">
            <div className="bg-gray-100 p-12 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
              <h2 className="text-xl font-medium text-gray-700 mb-2">WBS Structure</h2>
              <p className="text-gray-500">
                Work Breakdown Structure hierarchy will be displayed here.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleDetail; 

